import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { v4 as uuid } from "uuid";

import { IClassroomResource } from "../../classrooms";
import { EntityTypes } from "../../EntityTypes";
import { IMediaResource } from "../../media";
import { useApi } from "../../SDKProvider";
import { ITagResource } from "../../tags";
import { unwrapAPIResult } from "../../utils/api";
import { mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";
import { IResourceResource } from "../types";

export interface IResourceCreateMutationParameters {
    title: string;
    description: string;
    url: string;
    classroom: RelationalValue<IClassroomResource>;
    image?: RelationalValue<IMediaResource>;
    tags?: RelationalValue<ITagResource>[];
    uuid?: string;
}

export type IResourceCreateMutationResult = IResourceResource;

/**
 * Specialized hook for creating a resource with automatic image upload.
 */
export const useResourceCreateMutation = (
    options: UseMutationOptions<IResourceCreateMutationResult, unknown, IResourceCreateMutationParameters> = {},
) => {
    const api = useApi();

    const mutationFn = async (params: IResourceCreateMutationParameters) => {
        const { title, description, url, classroom, image, tags, uuid: providedUuid } = params;

        // Automatically upload image from URL (if no image provided)
        let mediaIRI = image ? mapRelationalValueAsIRI(EntityTypes.MEDIA, image) : undefined;

        if (url && !image) {
            try {
                // Retrieve preview image from URL
                const { data: imageBlob } = await axios.get("/api/website-image", {
                    params: { url },
                    responseType: "blob",
                });

                // Upload the image as media
                const formData = new FormData();
                formData.append("file", imageBlob);
                formData.append("uuid", uuid());

                const mediaResponse = await api.request<IMediaResource>({
                    url: "/media",
                    method: "POST",
                    headers: { "Content-Type": "multipart/form-data" },
                    data: formData,
                });

                mediaIRI = mediaResponse.data["@id"];
            } catch {
                mediaIRI = undefined;
            }
        }

        // Créer la resource avec le média attaché
        return unwrapAPIResult(
            api.request<IResourceCreateMutationResult>({
                method: "POST",
                url: "/resources",
                data: {
                    title,
                    description,
                    url,
                    classroom: mapRelationalValueAsIRI(EntityTypes.CLASSROOM, classroom),
                    image: mediaIRI,
                    tags: tags?.map(tag => mapRelationalValueAsIRI(EntityTypes.TAG, tag)),
                    uuid: providedUuid ?? uuid(),
                },
            }),
        );
    };

    return useMutation(mutationFn, options);
};
